import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { FaUserFriends, FaInfoCircle, FaPenNib } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const fieldVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export default function RegisterPage3({ onSubmit, onPrevious }) {
  const LOCAL_KEY = "register_step3";
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(LOCAL_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          disability: "",
          disabilityDetails: "",
          criminalConviction: "",
          criminalConvictionDetails: "",
          personalStatement: "",
        };
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(formData));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-4 px-3 sm:px-4 lg:px-6">
      <div className="w-full max-w-6xl bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Confirmation & Submission
              </h1>
            </div>

            {/* Show button only if token does NOT exist */}
            {!localStorage.getItem("token") && (
              <button
                onClick={() => {
                  localStorage.clear();
                  navigate("/");
                }}
                className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-white text-blue-700 font-semibold hover:bg-blue-50 hover:shadow transition-all duration-300 text-xs whitespace-nowrap self-center sm:self-auto"
              >
                <span>Already registered?</span>
                <span className="font-bold">Login</span>
              </button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto max-h-[75vh] p-4 sm:p-6 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Disability Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaUserFriends className="text-blue-600 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Disability</h3>
              </div>
              <motion.div
                variants={fieldVariants}
                initial="hidden"
                animate="visible"
                className="space-y-3"
              >
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">
                    Do you have a disability as defined under the Disability
                    Discrimination Act?
                  </label>
                  <div className="flex gap-6">
                    {["Yes", "No"].map((opt) => (
                      <label
                        key={opt}
                        className="inline-flex items-center space-x-2"
                      >
                        <input
                          type="radio"
                          name="disability"
                          value={opt}
                          onChange={handleChange}
                          checked={formData.disability === opt}
                          className="h-3.5 w-3.5 text-indigo-600 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <AnimatePresence>
                  {formData.disability === "Yes" && (
                    <motion.textarea
                      key="disabilityDetails"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      name="disabilityDetails"
                      placeholder="Please provide details about your disability..."
                      value={formData.disabilityDetails}
                      onChange={handleChange}
                      rows="3"
                      className="w-full text-sm border border-slate-300 bg-white px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Criminal Conviction Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaInfoCircle className="text-blue-600 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Criminal Conviction
                </h3>
              </div>
              <motion.div
                variants={fieldVariants}
                initial="hidden"
                animate="visible"
                className="space-y-3"
              >
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">
                    Do you have any criminal conviction(s)?
                  </label>
                  <div className="flex gap-6">
                    {["Yes", "No"].map((opt) => (
                      <label
                        key={opt}
                        className="inline-flex items-center space-x-2"
                      >
                        <input
                          type="radio"
                          name="criminalConviction"
                          value={opt}
                          onChange={handleChange}
                          checked={formData.criminalConviction === opt}
                          className="h-3.5 w-3.5 text-blue-600 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <AnimatePresence>
                  {formData.criminalConviction === "Yes" && (
                    <motion.textarea
                      key="criminalConvictionDetails"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      name="criminalConvictionDetails"
                      placeholder="Please provide details about your criminal conviction..."
                      value={formData.criminalConvictionDetails}
                      onChange={handleChange}
                      rows="3"
                      className="w-full text-sm border border-slate-300 bg-white px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition"
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Personal Statement Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-1.5 rounded-md mr-3">
                  <FaPenNib className="text-blue-600 text-sm" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Personal Statement
                </h3>
              </div>
              <motion.div
                variants={fieldVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                <textarea
                  name="personalStatement"
                  placeholder="Please provide your statement..."
                  value={formData.personalStatement}
                  onChange={handleChange}
                  maxLength="500"
                  rows="6"
                  className="w-full text-sm border border-slate-300 bg-white px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition"
                />
                <div className="text-xs text-slate-500">
                  Statements should be approximately 500 words and will be
                  scrutinised for plagiarism.
                </div>
                <div className="text-right text-xs text-slate-500">
                  {formData.personalStatement.length} / 500 characters
                </div>
              </motion.div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
              {/* Previous Button */}
              <button
                type="button"
                onClick={onPrevious}
                className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-300 flex-1 text-sm"
              >
                Previous
              </button>

              {/* Submit Button with motion */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg font-medium text-white hover:opacity-90 transition-all duration-300 flex-1 text-sm"
              >
                Submit
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}